import { useEffect, useState, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button, FormText, Row, Col
} from 'reactstrap';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { FileUploader } from '../Components/FileUploader';
import { moveInventoryItem, addItemToVendor } from '../Helpers/Api';
import { formatDateForInput } from '../Helpers/Utils';
import { getRequestTypes, getAllRMVendors } from '../../RMVendors/Helpers/Api';

const AddEditInventoryItemModal = ({
  mode,
  open,
  onClose,
  formik,
  propertyList,
  selectedProperty,
  itemId,
  user
}) => {
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [requestTypes, setRequestTypes] = useState([]);

  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';

  const fetchRequestTypes = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await getRequestTypes(user?.company_id || user?.id || '');
      setRequestTypes(data);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, user?.company_id, user?.id]);

  useEffect(() => {
    if (open && isAddMode) {
      setActiveIndex(0);
    }
    if (open) {
      fetchRequestTypes();
    }
  }, [open, isAddMode, fetchRequestTypes]);

  const requestTypeOptions = requestTypes.map(type => ({
    value: type._id,
    label: type.request_type || type.name || type.label || 'Unknown'
  }));

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'under_maintenance', label: 'Under Maintenance' },
    { value: 'retired', label: 'Retired' },
    { value: 'not_applicable', label: 'N/A' }
  ];

  const handleAddItem = () => {
    const newItems = [
      ...(formik.values.items || []),
      {
        name: '',
        make: '',
        modelNo: '',
        sku: [],
        requestsSupported: [],
        quantity: {
          active: '',
          min: '',
          max: '',
        },
        costPerUnit: '',
        warrantyDetails: [
          {
            boughtOn: undefined,
            boughtFrom: undefined,
            warrantyExpirationDate: '',
          },
        ],
        locationDetails: {
          room: '',
          notes: '',
        },
        images: [
          {
            url: '',
            uploadedBy: {
              id: '',
              role: '',
            },
          },
        ],
        invoices: [
          {
            url: '',
            uploadedBy: {
              id: '',
              role: '',
            },
          },
        ],
        status: 'active',
      },
    ];
    formik.setFieldValue('items', newItems);
    setActiveIndex(newItems.length - 1);
  };

  const handleRemoveItem = (index) => {
    const currentItems = formik.values.items || [];
    if (currentItems.length <= 1) return;

    const newItems = currentItems.filter((_, i) => i !== index);
    formik.setFieldValue('items', newItems);
    setActiveIndex(Math.min(activeIndex, newItems.length - 1));
  };

  const getFieldValue = (field, defaultValue = '') => {
    if (isAddMode) {
      const value = formik.values.items?.[activeIndex]?.[field] ?? defaultValue;
      if (field === 'status') {
        return statusOptions.find(option => option.value === value) || null;
      }
      return value;
    }
    const value = formik.values.item?.[field] ?? defaultValue;
    if (field === 'status') {
      return statusOptions.find(option => option.value === value) || null;
    }
    return value;
  };

  const getNestedFieldValue = (nestedField, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.[nestedField]?.[field] ?? defaultValue;
    }
    return formik.values.item?.[nestedField]?.[field] ?? defaultValue;
  };

  const getWarrantyFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.warrantyDetails?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.warrantyDetails?.[index]?.[field] ?? defaultValue;
  };

  const getImageFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.images?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.images?.[index]?.[field] ?? defaultValue;
  };

  const getInvoiceFieldValue = (index, field, defaultValue = '') => {
    if (isAddMode) {
      return formik.values.items?.[activeIndex]?.invoices?.[index]?.[field] ?? defaultValue;
    }
    return formik.values.item?.invoices?.[index]?.[field] ?? defaultValue;
  };

  const handleFieldChange = (field) => (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].${field}`, value);
    } else {
      formik.setFieldValue(`item.${field}`, value);
    }
  };

  const handleInputFieldChange = (e) => {
    const { name, value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].${name}`, value);
    } else {
      formik.setFieldValue(`item.${name}`, value);
    }
  };

  const handleNestedFieldChange = (nestedField, field) => (e) => {
    const { value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].${nestedField}.${field}`, value);
    } else {
      formik.setFieldValue(`item.${nestedField}.${field}`, value);
    }
  };

  const handleWarrantyFieldChange = (index, field) => (e) => {
    const { value } = e.target;
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].warrantyDetails[${index}].${field}`, value);
    } else {
      formik.setFieldValue(`item.warrantyDetails[${index}].${field}`, value);
    }
  };

  const handleRequestsSupportedChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].requestsSupported`, selectedValues);
    } else {
      formik.setFieldValue(`item.requestsSupported`, selectedValues);
    }
  };

  const getCurrentRequestsSupported = () => {
    let currentValues = getFieldValue('requestsSupported', []);
    if (Array.isArray(currentValues)) {
      currentValues = currentValues.map(val => {
        if (typeof val === 'object' && val._id) {
          return val._id;
        }
        return val;
      });
    }
    return requestTypeOptions.filter(option => currentValues.includes(option.value));
  };

  const handleSkuChange = (e) => {
    const value = e.target.value;
    const skuArray = value ? value.split(',').map(s => s.trim()) : [];
    if (isAddMode) {
      formik.setFieldValue(`items[${activeIndex}].sku`, skuArray);
    } else {
      formik.setFieldValue(`item.sku`, skuArray);
    }
  };

  const getFieldError = (field) => {
    if (isAddMode) {
      const itemErrors = formik.errors.items?.[activeIndex];
      const itemTouched = formik.touched.items?.[activeIndex];
      return itemTouched?.[field] && itemErrors?.[field] ? itemErrors[field] : null;
    }
    return formik.touched.item?.[field] && formik.errors.item?.[field] ? formik.errors.item[field] : null;
  };

  const getNestedFieldError = (nestedField, field) => {
    if (isAddMode) {
      const itemErrors = formik.errors.items?.[activeIndex]?.[nestedField];
      const itemTouched = formik.touched.items?.[activeIndex]?.[nestedField];
      return itemTouched?.[field] && itemErrors?.[field] ? itemErrors[field] : null;
    }
    return formik.touched.item?.[nestedField]?.[field] && formik.errors.item?.[nestedField]?.[field]
      ? formik.errors.item[nestedField][field] : null;
  };

  const getWarrantyFieldError = (index, field) => {
    if (isAddMode) {
      const warrantyErrors = formik.errors.items?.[activeIndex]?.warrantyDetails?.[index];
      const warrantyTouched = formik.touched.items?.[activeIndex]?.warrantyDetails?.[index];
      return warrantyTouched?.[field] && warrantyErrors?.[field] ? warrantyErrors[field] : null;
    }
    return formik.touched.item?.warrantyDetails?.[index]?.[field] && formik.errors.item?.warrantyDetails?.[index]?.[field]
      ? formik.errors.item.warrantyDetails[index][field] : null;
  };

  const handleFieldBlur = (field) => () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].${field}`, true);
    } else {
      formik.setFieldTouched(`item.${field}`, true);
    }
  };

  const handleInputFieldBlur = (e) => {
    const { name } = e.target;
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].${name}`, true);
    } else {
      formik.setFieldTouched(`item.${name}`, true);
    }
  };

  const handleNestedFieldBlur = (nestedField, field) => () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].${nestedField}.${field}`, true);
    } else {
      formik.setFieldTouched(`item.${nestedField}.${field}`, true);
    }
  };

  const handleWarrantyFieldBlur = (index, field) => () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].warrantyDetails[${index}].${field}`, true);
    } else {
      formik.setFieldTouched(`item.warrantyDetails[${index}].${field}`, true);
    }
  };

  const handleRequestsSupportedBlur = () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].requestsSupported`, true);
    } else {
      formik.setFieldTouched(`item.requestsSupported`, true);
    }
  };

  const handleSkuBlur = () => {
    if (isAddMode) {
      formik.setFieldTouched(`items[${activeIndex}].sku`, true);
    } else {
      formik.setFieldTouched(`item.sku`, true);
    }
  };

  const handleAddWarranty = () => {
    const newWarranty = {
      boughtOn: undefined,
      boughtFrom: undefined,
      warrantyExpirationDate: '',
    };
    if (isAddMode) {
      const currentWarranties = formik.values.items?.[activeIndex]?.warrantyDetails || [];
      formik.setFieldValue(`items[${activeIndex}].warrantyDetails`, [...currentWarranties, newWarranty]);
    } else {
      const currentWarranties = formik.values.item?.warrantyDetails || [];
      formik.setFieldValue('item.warrantyDetails', [...currentWarranties, newWarranty]);
    }
  };

  const handleRemoveWarranty = (index) => {
    if (isAddMode) {
      const currentWarranties = formik.values.items?.[activeIndex]?.warrantyDetails || [];
      formik.setFieldValue(
        `items[${activeIndex}].warrantyDetails`,
        currentWarranties.filter((_, i) => i !== index)
      );
    } else {
      const currentWarranties = formik.values.item?.warrantyDetails || [];
      formik.setFieldValue(
        'item.warrantyDetails',
        currentWarranties.filter((_, i) => i !== index)
      );
    }
  };

  const handleAddImage = () => {
    const newImage = {
      url: '',
      uploadedBy: {
        id: '',
        role: '',
      },
    };
    if (isAddMode) {
      const currentImages = formik.values.items?.[activeIndex]?.images || [];
      formik.setFieldValue(`items[${activeIndex}].images`, [...currentImages, newImage]);
    } else {
      const currentImages = formik.values.item?.images || [];
      formik.setFieldValue('item.images', [...currentImages, newImage]);
    }
  };

  const handleRemoveImage = (index) => {
    if (isAddMode) {
      const currentImages = formik.values.items?.[activeIndex]?.images || [];
      formik.setFieldValue(
        `items[${activeIndex}].images`,
        currentImages.filter((_, i) => i !== index)
      );
    } else {
      const currentImages = formik.values.item?.images || [];
      formik.setFieldValue(
        'item.images',
        currentImages.filter((_, i) => i !== index)
      );
    }
  };

  const handleAddInvoice = () => {
    const newInvoice = {
      url: '',
      uploadedBy: {
        id: '',
        role: '',
      },
    };
    if (isAddMode) {
      const currentInvoices = formik.values.items?.[activeIndex]?.invoices || [];
      formik.setFieldValue(`items[${activeIndex}].invoices`, [...currentInvoices, newInvoice]);
    } else {
      const currentInvoices = formik.values.item?.invoices || [];
      formik.setFieldValue('item.invoices', [...currentInvoices, newInvoice]);
    }
  };

  const handleRemoveInvoice = (index) => {
    if (isAddMode) {
      const currentInvoices = formik.values.items?.[activeIndex]?.invoices || [];
      formik.setFieldValue(
        `items[${activeIndex}].invoices`,
        currentInvoices.filter((_, i) => i !== index)
      );
    } else {
      const currentInvoices = formik.values.item?.invoices || [];
      formik.setFieldValue(
        'item.invoices',
        currentInvoices.filter((_, i) => i !== index)
      );
    }
  };

  const handleMoveItem = async () => {
    if (!isEditMode || !itemId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Move functionality is only available in edit mode with a valid item ID.',
      });
      return;
    }

    const currentQuantity = formik.values.item?.quantity?.active || 1;
    const propertyOptions = propertyList.map(prop => ({
      value: prop._id,
      label: `${prop.title}, ${prop.location}`,
    }));

    let selectedPropertyId = '';
    let selectedBuildingName = '';
    let buildingOptions = [];

    const updateBuildingOptions = (propertyId) => {
      const selectedProperty = propertyList.find(prop => prop._id === propertyId);
      return selectedProperty?.building
        ?.filter(b => b && b.buildingName)
        ?.map(b => ({
          value: b.buildingName,
          label: b.buildingName,
        })) || [];
    };

    await Swal.fire({
      title: 'Move Inventory Item',
      width: '700px',
      html: `
      <div>
        <label for="destination_property_id" style="display: block; margin-bottom: 5px;">Destination Property *</label>
        <select id="destination_property_id" class="swal2-select" style="width: 80%; padding: 8px;">
          <option value="">Select Property</option>
          ${propertyOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
        </select>
        <label for="destination_building_name" style="display: block; margin-top: 15px; margin-bottom: 5px;">Building Name *</label>
        <select id="destination_building_name" class="swal2-select" style="width: 80%; padding: 8px;" disabled>
          <option value="">Select Building</option>
        </select>
        <label for="quantity" style="display: block; margin-top: 15px; margin-bottom: 5px;">Active Quantity (Max: ${currentQuantity})</label>
        <input type="number" id="movable_quantity" class="swal2-input" placeholder="Enter quantity" min="1" max="${currentQuantity}" style="width: 80%;">
      </div>
    `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Move',
      preConfirm: () => {
        const propertyId = document.getElementById('destination_property_id').value;
        const buildingName = document.getElementById('destination_building_name').value;
        const quantity = document.getElementById('movable_quantity').value;

        const quantityNum = parseInt(quantity, 10);

        if (!propertyId) {
          Swal.showValidationMessage('Please select a destination property');
          return false;
        }
        if (!buildingName) {
          Swal.showValidationMessage('Please select a building name');
          return false;
        }
        if (!quantity || quantity.trim() === '') {
          Swal.showValidationMessage('Please enter a quantity');
          return false;
        }
        if (isNaN(quantityNum)) {
          Swal.showValidationMessage('Quantity must be a valid number');
          return false;
        }
        if (quantityNum <= 0) {
          Swal.showValidationMessage('Quantity must be greater than 0');
          return false;
        }
        if (quantityNum > currentQuantity) {
          Swal.showValidationMessage(`Quantity cannot exceed ${currentQuantity}`);
          return false;
        }

        return {
          item_id: itemId,
          destination_property_id: propertyId,
          destination_building_name: buildingName,
          quantity: quantityNum,
        };
      },
      didOpen: () => {
        const propertySelect = document.getElementById('destination_property_id');
        const buildingSelect = document.getElementById('destination_building_name');
        const quantityInput = document.getElementById('movable_quantity');

        quantityInput.addEventListener('input', (e) => {
          let val = parseInt(e.target.value, 10);
          if (val > currentQuantity) e.target.value = currentQuantity;
          if (val < 1) e.target.value = 1;
        });

        propertySelect.addEventListener('change', (e) => {
          selectedPropertyId = e.target.value;
          buildingOptions = updateBuildingOptions(selectedPropertyId);
          buildingSelect.disabled = buildingOptions.length === 0;
          buildingSelect.innerHTML = `
          <option value="">Select Building</option>
          ${buildingOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
        `;
          if (buildingOptions.length === 1) {
            buildingSelect.value = buildingOptions[0].value;
            selectedBuildingName = buildingOptions[0].value;
          }
        });

        buildingSelect.addEventListener('change', (e) => {
          selectedBuildingName = e.target.value;
        });
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          dispatch({ type: 'SET_LOADING', payload: true });
          const response = await moveInventoryItem(result.value);
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: response.message || 'Inventory item moved successfully',
          });
          onClose();
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'Failed to move inventory item',
          });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    });
  };

  const handleAddItemToVendor = async () => {
  if (!isEditMode || !itemId) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Add to vendor functionality is only available in edit mode with a valid item ID.',
      confirmButtonText: 'OK',
      customClass: {
        confirmButton: 'vendor-modal-confirm-btn'
      },
      buttonsStyling: false
    });
    return;
  }

  const propertyId = selectedProperty?.property_id;
  if (!propertyId) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Property ID is required to fetch vendors.',
      confirmButtonText: 'OK',
      customClass: {
        confirmButton: 'vendor-modal-confirm-btn'
      },
      buttonsStyling: false
    });
    return;
  }

  let vendorOptions = [];
  try {
    dispatch({ type: 'SET_LOADING', payload: true });
    const vendorData = await getAllRMVendors(propertyId);
    vendorOptions = vendorData.data?.map(vendor => ({
      value: vendor._id,
      label: vendor.companyName || 'Unknown Vendor',
    })) || [];
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'Failed to fetch vendors',
      confirmButtonText: 'OK',
      customClass: {
        confirmButton: 'vendor-modal-confirm-btn'
      },
      buttonsStyling: false
    });
    dispatch({ type: 'SET_LOADING', payload: false });
    return;
  } finally {
    dispatch({ type: 'SET_LOADING', payload: false });
  }

  await Swal.fire({
    title: 'Add Item to Vendor',
    icon: 'question',
    width: '500px',
    showCancelButton: true,
    confirmButtonText: '<i class="fas fa-plus"></i> Add to Vendor',
    cancelButtonText: '<i class="fas fa-times"></i> Cancel',
    buttonsStyling: false,
    customClass: {
      popup: 'vendor-modal-popup',
      title: 'vendor-modal-title',
      htmlContainer: 'vendor-modal-content',
      confirmButton: 'vendor-modal-confirm-btn',
      cancelButton: 'vendor-modal-cancel-btn',
    },
    html: `
      <style>
        .vendor-modal-popup {
          border-radius: 12px !important;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }

        .vendor-modal-title {
          font-size: 24px !important;
          font-weight: 600 !important;
          color: #2d3748 !important;
          margin-bottom: 10px !important;
        }

        .vendor-modal-content {
          padding: 0 20px 20px 20px !important;
          text-align: left !important;
        }

        .vendor-form-container {
          background: #f8fafc;
          border-radius: 8px;
          padding: 20px;
          margin: 15px 0;
        }

        .vendor-description {
          color: #4a5568;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 20px;
          text-align: center;
        }

        .vendor-form-group {
          margin-bottom: 20px;
        }

        .vendor-form-label {
          display: block;
          font-weight: 600;
          font-size: 14px;
          color: #2d3748;
          margin-bottom: 8px;
        }

        .vendor-required {
          color: #e53e3e;
          font-weight: 700;
        }

        .vendor-select {
          width: 100% !important;
          padding: 12px 16px !important;
          border: 2px solid #e2e8f0 !important;
          border-radius: 8px !important;
          font-size: 14px !important;
          color: #2d3748 !important;
          background: white !important;
          transition: all 0.2s ease !important;
          outline: none !important;
        }

        .vendor-select:focus {
          border-color: #3182ce !important;
          box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1) !important;
        }

        .vendor-select:hover {
          border-color: #cbd5e0 !important;
        }

        .vendor-info {
          background: #ebf8ff;
          border: 1px solid #bee3f8;
          border-radius: 6px;
          padding: 12px;
          font-size: 12px;
          color: #2b6cb0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .vendor-info::before {
          content: "ℹ️";
          font-size: 14px;
        }

        .vendor-modal-confirm-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          border: none !important;
          padding: 12px 24px !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          margin: 0 8px !important;
        }

        .vendor-modal-confirm-btn:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
        }

        .vendor-modal-cancel-btn {
          background: #f7fafc !important;
          color: #4a5568 !important;
          border: 2px solid #e2e8f0 !important;
          padding: 12px 24px !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          margin: 0 8px !important;
        }

        .vendor-modal-cancel-btn:hover {
          background: #edf2f7 !important;
          border-color: #cbd5e0 !important;
        }

        .swal2-validation-message {
          background: #fed7d7 !important;
          color: #c53030 !important;
          border-radius: 6px !important;
          padding: 8px 12px !important;
          font-size: 13px !important;
          margin-top: 8px !important;
        }
      </style>

      <div class="vendor-form-container">
        <p class="vendor-description">
          Select a vendor from the list below to assign this inventory item.
        </p>

        <div class="vendor-form-group">
          <label for="vendor_id" class="vendor-form-label">
            Choose Vendor <span class="vendor-required">*</span>
          </label>
          <select id="vendor_id" class="vendor-select">
            <option value="">-- Select a Vendor --</option>
            ${vendorOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
          </select>
        </div>

        <div class="vendor-info">
          Only vendors associated with the selected property are shown.
        </div>
      </div>
    `,
    focusConfirm: false,
    preConfirm: () => {
      const vendorId = document.getElementById('vendor_id').value;
      if (!vendorId) {
        Swal.showValidationMessage('Please select a vendor to continue');
        return false;
      }

      return {
        vendor_id: vendorId,
        inventory_item_id: itemId,
      };
    },
    didOpen: () => {
      const vendorSelect = document.getElementById('vendor_id');
      vendorSelect.addEventListener('change', () => {
        const validationMessage = document.querySelector('.swal2-validation-message');
        if (validationMessage) {
          validationMessage.style.display = 'none';
        }
      });
    }
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const response = await addItemToVendor(result.value);

        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: response.message || 'Item has been successfully added to the vendor',
          confirmButtonText: 'Great!',
          customClass: {
            confirmButton: 'vendor-modal-confirm-btn'
          },
          buttonsStyling: false
        });

        onClose();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          text: error.message || 'Failed to add item to vendor. Please try again.',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'vendor-modal-confirm-btn'
          },
          buttonsStyling: false
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  });
};

  const count = isAddMode ? (formik.values.items?.length ?? 0) : 0;
  const warrantyDetails = isAddMode
    ? formik.values.items?.[activeIndex]?.warrantyDetails || []
    : formik.values.item?.warrantyDetails || [];
  const images = isAddMode
    ? formik.values.items?.[activeIndex]?.images || []
    : formik.values.item?.images || [];
  const invoices = isAddMode
    ? formik.values.items?.[activeIndex]?.invoices || []
    : formik.values.item?.invoices || [];

  return (
    <Modal isOpen={open} toggle={onClose} size="xl">
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={onClose}>
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {isEditMode ? 'Edit Inventory Item' : 'Add Inventory Item'}
              {isAddMode && count > 1 && ` (${count})`}
            </span>
            {isEditMode && (
              <div>
                <Button
                  type="button"
                  color="warning"
                  size="sm"
                  onClick={handleMoveItem}
                  disabled={formik.isSubmitting}
                  className="ms-3"
                >
                  Move Item
                </Button>
                <Button
                  type="button"
                  color="info"
                  size="sm"
                  onClick={handleAddItemToVendor}
                  disabled={formik.isSubmitting}
                  className="ms-3"
                >
                  Add to Vendor
                </Button>
              </div>
            )}
          </div>
        </ModalHeader>

        {isAddMode && count > 1 && (
          <div className="px-3 pt-2 border-bottom">
            <Row className="mb-2">
              <Col>
                <div className="d-flex flex-nowrap overflow-auto" style={{ gap: '8px' }}>
                  {formik.values.items.map((_, index) => (
                    <Button
                      key={index}
                      type="button"
                      color={activeIndex === index ? 'primary' : 'secondary'}
                      outline={activeIndex !== index}
                      size="sm"
                      onClick={() => setActiveIndex(index)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Item {index + 1}
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col>
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddItem}
                  className="me-2"
                >
                  Add Item
                </Button>
                {count > 1 && (
                  <Button
                    type="button"
                    color="danger"
                    size="sm"
                    onClick={() => handleRemoveItem(activeIndex)}
                  >
                    Remove Current
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        )}

        <ModalBody>
          <Row className="mb-3">
            <Col md={3}>
              <FormGroup>
                <Label for="name" style={{ fontSize: '0.875rem' }}>Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={getFieldValue('name')}
                  onChange={handleInputFieldChange}
                  onBlur={handleInputFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('name')}
                  required
                />
                {getFieldError('name') && (
                  <FormText color="danger">{getFieldError('name')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="make" style={{ fontSize: '0.875rem' }}>Make</Label>
                <Input
                  id="make"
                  name="make"
                  value={getFieldValue('make')}
                  onChange={handleInputFieldChange}
                  onBlur={handleInputFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('make')}
                />
                {getFieldError('make') && (
                  <FormText color="danger">{getFieldError('make')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="modelNo" style={{ fontSize: '0.875rem' }}>Model Number</Label>
                <Input
                  id="modelNo"
                  name="modelNo"
                  value={getFieldValue('modelNo')}
                  onChange={handleInputFieldChange}
                  onBlur={handleInputFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('modelNo')}
                />
                {getFieldError('modelNo') && (
                  <FormText color="danger">{getFieldError('modelNo')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="requestsSupported" style={{ fontSize: '0.875rem' }}>Requests Supported *</Label>
                <Select
                  id="requestsSupported"
                  isMulti
                  options={requestTypeOptions}
                  value={getCurrentRequestsSupported()}
                  onChange={handleRequestsSupportedChange}
                  onBlur={handleRequestsSupportedBlur}
                  isDisabled={formik.isSubmitting}
                  placeholder="Select supported requests..."
                  className={getFieldError('requestsSupported') ? 'is-invalid' : ''}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: getFieldError('requestsSupported') ? '#dc3545' : base.borderColor,
                      '&:hover': {
                        borderColor: getFieldError('requestsSupported') ? '#dc3545' : base.borderColor,
                      },
                    }),
                  }}
                />
                {getFieldError('requestsSupported') && (
                  <FormText color="danger">{getFieldError('requestsSupported')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">

            <Col md={3}>
              <FormGroup>
                <Label for="sku" style={{ fontSize: '0.875rem' }}>SKU (comma-separated)</Label>
                <Input
                  id="sku"
                  name="sku"
                  value={getFieldValue('sku', []).join(', ')}
                  onChange={handleSkuChange}
                  onBlur={handleSkuBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('sku')}
                />
                {getFieldError('sku') && (
                  <FormText color="danger">{getFieldError('sku')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="costPerUnit" style={{ fontSize: '0.875rem' }}>Cost Per Unit *</Label>
                <Input
                  id="costPerUnit"
                  name="costPerUnit"
                  type="number"
                  min="0"
                  step="0.01"
                  value={getFieldValue('costPerUnit', '')}
                  onChange={handleInputFieldChange}
                  onBlur={handleInputFieldBlur}
                  disabled={formik.isSubmitting}
                  invalid={!!getFieldError('costPerUnit')}
                  required
                />
                {getFieldError('costPerUnit') && (
                  <FormText color="danger">{getFieldError('costPerUnit')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="quantity.active" style={{ fontSize: '0.875rem' }}>Active Quantity *</Label>
                <Input
                  id="quantity.active"
                  name="quantity.active"
                  type="number"
                  min="0"
                  value={getNestedFieldValue('quantity', 'active', '')}
                  onChange={handleNestedFieldChange('quantity', 'active')}
                  onBlur={handleNestedFieldBlur('quantity', 'active')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('quantity', 'active')}
                  required
                />
                {getNestedFieldError('quantity', 'active') && (
                  <FormText color="danger">{getNestedFieldError('quantity', 'active')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="quantity.min" style={{ fontSize: '0.875rem' }}>Min Quantity</Label>
                <Input
                  id="quantity.min"
                  name="quantity.min"
                  type="number"
                  min="0"
                  value={getNestedFieldValue('quantity', 'min', '')}
                  onChange={handleNestedFieldChange('quantity', 'min')}
                  onBlur={handleNestedFieldBlur('quantity', 'min')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('quantity', 'min')}
                />
                {getNestedFieldError('quantity', 'min') && (
                  <FormText color="danger">{getNestedFieldError('quantity', 'min')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="quantity.max" style={{ fontSize: '0.875rem' }}>Max Quantity</Label>
                <Input
                  id="quantity.max"
                  name="quantity.max"
                  type="number"
                  min="0"
                  value={getNestedFieldValue('quantity', 'max', '')}
                  onChange={handleNestedFieldChange('quantity', 'max')}
                  onBlur={handleNestedFieldBlur('quantity', 'max')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('quantity', 'max')}
                />
                {getNestedFieldError('quantity', 'max') && (
                  <FormText color="danger">{getNestedFieldError('quantity', 'max')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <FormGroup>
                <Label for="locationDetails.room" style={{ fontSize: '0.875rem' }}>Location - Room</Label>
                <Input
                  name="room"
                  value={getNestedFieldValue('locationDetails', 'room')}
                  onChange={handleNestedFieldChange('locationDetails', 'room')}
                  onBlur={handleNestedFieldBlur('locationDetails', 'room')}
                  disabled={formik.isSubmitting}
                  invalid={!!getNestedFieldError('locationDetails', 'room')}
                />
                {getNestedFieldError('locationDetails', 'room') && (
                  <FormText color="danger">{getNestedFieldError('locationDetails', 'room')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="locationDetails.notes" style={{ fontSize: '0.875rem' }}>Notes</Label>
                <Input
                  name="notes"
                  type="textarea"
                  value={getNestedFieldValue('locationDetails', 'notes')}
                  onChange={handleNestedFieldChange('locationDetails', 'notes')}
                  onBlur={handleNestedFieldBlur('locationDetails', 'notes')}
                  disabled={formik.isSubmitting}
                  rows={1}
                />
                {getNestedFieldError('locationDetails', 'notes') && (
                  <FormText color="danger">{getNestedFieldError('locationDetails', 'notes')}</FormText>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="status" style={{ fontSize: '0.875rem' }}>Status</Label>
                <Select
                  id="status"
                  options={statusOptions}
                  value={getFieldValue('status')}
                  onChange={handleFieldChange('status')}
                  onBlur={handleFieldBlur('status')}
                  isDisabled={formik.isSubmitting}
                  placeholder="Select Status"
                  className={getFieldError('status') ? 'is-invalid' : ''}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: getFieldError('status') ? '#dc3545' : base.borderColor,
                      '&:hover': {
                        borderColor: getFieldError('status') ? '#dc3545' : base.borderColor,
                      },
                    }),
                  }}
                />
                {getFieldError('status') && (
                  <FormText color="danger">{getFieldError('status')}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>

          <div className="mb-3">
            {warrantyDetails.map((_, index) => (
              <div key={index} className="border p-3 mb-3 rounded">
                <Row className="mb-2">
                  <Col>
                    <h6 style={{ fontSize: '0.875rem' }}>Warranty Details {index + 1}</h6>
                  </Col>
                  <Col xs="auto">
                    <Button
                      type="button"
                      color="success"
                      size="sm"
                      className='me-2'
                      onClick={handleAddWarranty}
                      disabled={formik.isSubmitting}
                    >
                      Add Warranty
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveWarranty(index)}
                      disabled={formik.isSubmitting}
                    >
                      Remove Warranty
                    </Button>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Bought On</Label>
                      <Input
                        type="date"
                        value={formatDateForInput(getWarrantyFieldValue(index, 'boughtOn'))}
                        onChange={handleWarrantyFieldChange(index, 'boughtOn')}
                        onBlur={handleWarrantyFieldBlur(index, 'boughtOn')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'boughtOn')}
                      />
                      {getWarrantyFieldError(index, 'boughtOn') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'boughtOn')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Bought From</Label>
                      <Input
                        value={getWarrantyFieldValue(index, 'boughtFrom')}
                        onChange={handleWarrantyFieldChange(index, 'boughtFrom')}
                        onBlur={handleWarrantyFieldBlur(index, 'boughtFrom')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'boughtFrom')}
                      />
                      {getWarrantyFieldError(index, 'boughtFrom') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'boughtFrom')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label style={{ fontSize: '0.875rem' }}>Warranty Expiration Date</Label>
                      <Input
                        type="date"
                        value={formatDateForInput(getWarrantyFieldValue(index, 'warrantyExpirationDate'))}
                        onChange={handleWarrantyFieldChange(index, 'warrantyExpirationDate')}
                        onBlur={handleWarrantyFieldBlur(index, 'warrantyExpirationDate')}
                        disabled={formik.isSubmitting}
                        invalid={!!getWarrantyFieldError(index, 'warrantyExpirationDate')}
                      />
                      {getWarrantyFieldError(index, 'warrantyExpirationDate') && (
                        <FormText color="danger">{getWarrantyFieldError(index, 'warrantyExpirationDate')}</FormText>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            ))}
            {warrantyDetails.length === 0 && (
              <Button
                type="button"
                color="success"
                size="sm"
                onClick={handleAddWarranty}
                disabled={formik.isSubmitting}
              >
                Add Warranty
              </Button>
            )}
          </div>

          <Row className="mb-3">
            <Col md={6}>
              {images.map((_, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row className="mb-2">
                    <Col>
                      <h6 style={{ fontSize: '0.875rem' }}>Image {index + 1}</h6>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        className='me-2'
                        onClick={handleAddImage}
                        disabled={formik.isSubmitting}
                      >
                        Add Image
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveImage(index)}
                        disabled={formik.isSubmitting}
                      >
                        Remove Image
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <FileUploader
                        formik={formik}
                        fieldName={isAddMode ? `items[${activeIndex}].images[${index}].url` : `item.images[${index}].url`}
                        label="Image File"
                        isDisabled={formik.isSubmitting}
                        acceptedFileTypes="image/jpeg,image/png,image/gif,image/webp"
                      />
                    </Col>
                  </Row>
                </div>
              ))}
              {images.length === 0 && (
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddImage}
                  disabled={formik.isSubmitting}
                >
                  Add Image
                </Button>
              )}
            </Col>
            <Col md={6}>
              {invoices.map((_, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <Row className="mb-2">
                    <Col>
                      <h6 style={{ fontSize: '0.875rem' }}>Invoice {index + 1}</h6>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        className='me-2'
                        onClick={handleAddInvoice}
                        disabled={formik.isSubmitting}
                      >
                        Add Invoice
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveInvoice(index)}
                        disabled={formik.isSubmitting}
                      >
                        Remove Invoice
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <FileUploader
                        formik={formik}
                        fieldName={isAddMode ? `items[${activeIndex}].invoices[${index}].url` : `item.invoices[${index}].url`}
                        label="Invoice File"
                        isDisabled={formik.isSubmitting}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                      />
                    </Col>
                  </Row>
                </div>
              ))}
              {invoices.length === 0 && (
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={handleAddInvoice}
                  disabled={formik.isSubmitting}
                >
                  Add Invoice
                </Button>
              )}
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={formik.isSubmitting}
          >
            {isEditMode ? 'Update' : 'Save'}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={onClose}
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export { AddEditInventoryItemModal };